import { ChevronDown, ChevronRight, Search, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getProducts } from '@/api/api'
import { EpisodeList, Product } from '@/api/type'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

// 利用可能なドメイン一覧
const AVAILABLE_DOMAINS = ['line', 'tver', 'fod', 'abema', 'oreilly', 'archive', 'local', 'audiobook', 'apple_music']

type SelectedItem = {
  type: 'product' | 'episode'
  id: number
  title: string
  domain: string
  productTitle?: string // エピソードの場合、親プロダクトのタイトル
}

type AccessSettingsModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productIds: number[]
  episodeIds: number[]
  onSave: (productIds: number[], episodeIds: number[]) => void
}

export const AccessSettingsModal = ({
  open,
  onOpenChange,
  productIds,
  episodeIds,
  onSave,
}: AccessSettingsModalProps) => {
  const [domain, setDomain] = useState(AVAILABLE_DOMAINS[0])
  const [searchTerm, setSearchTerm] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set())
  const abortControllerRef = useRef<AbortController | null>(null)

  // 選択済みアイテム
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])

  // 初期化: productIds/episodeIdsから選択済みアイテムを復元
  useEffect(() => {
    if (open) {
      const items: SelectedItem[] = [
        ...productIds.map((id) => ({
          type: 'product' as const,
          id,
          title: `Product #${id}`,
          domain: '',
        })),
        ...episodeIds.map((id) => ({
          type: 'episode' as const,
          id,
          title: `Episode #${id}`,
          domain: '',
        })),
      ]
      setSelectedItems(items)
    }
  }, [open, productIds, episodeIds])

  // プロダクト検索
  const handleSearch = useCallback(async () => {
    // 前のリクエストをキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setLoading(true)
    try {
      const response = await getProducts(
        {
          domain,
          title: searchTerm || undefined,
          per_page: 50,
          with_episodes: true,
        },
        abortController.signal,
      )
      // キャンセルされていない場合のみ結果を反映
      if (!abortController.signal.aborted) {
        setProducts(response.products)
      }
    } catch (error) {
      // キャンセルエラーは無視
      if (error instanceof Error && error.name !== 'CanceledError') {
        console.error('Failed to fetch products:', error)
      }
    } finally {
      // キャンセルされていない場合のみローディング解除
      if (!abortController.signal.aborted) {
        setLoading(false)
      }
    }
  }, [domain, searchTerm])

  // ドメイン変更時に検索
  useEffect(() => {
    if (open) {
      handleSearch()
    }
  }, [open, domain, handleSearch])

  // プロダクトの展開/折りたたみ
  const toggleExpanded = (productId: number) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  // プロダクト選択
  const toggleProduct = (product: Product) => {
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.type === 'product' && item.id === product.product_id)
      if (exists) {
        return prev.filter((item) => !(item.type === 'product' && item.id === product.product_id))
      } else {
        return [
          ...prev,
          {
            type: 'product',
            id: product.product_id,
            title: product.title,
            domain: domain,
          },
        ]
      }
    })
  }

  // エピソード選択
  const toggleEpisode = (product: Product, episode: EpisodeList) => {
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.type === 'episode' && item.id === episode.episode_id)
      if (exists) {
        return prev.filter((item) => !(item.type === 'episode' && item.id === episode.episode_id))
      } else {
        return [
          ...prev,
          {
            type: 'episode',
            id: episode.episode_id,
            title: `#${episode.index}`,
            domain: domain,
            productTitle: product.title,
          },
        ]
      }
    })
  }

  // アイテム削除
  const removeItem = (item: SelectedItem) => {
    setSelectedItems((prev) => prev.filter((i) => !(i.type === item.type && i.id === item.id)))
  }

  // 保存
  const handleSave = () => {
    const newProductIds = selectedItems.filter((item) => item.type === 'product').map((item) => item.id)
    const newEpisodeIds = selectedItems.filter((item) => item.type === 'episode').map((item) => item.id)
    onSave(newProductIds, newEpisodeIds)
    onOpenChange(false)
  }

  // プロダクトが選択されているかチェック
  const isProductSelected = (productId: number) =>
    selectedItems.some((item) => item.type === 'product' && item.id === productId)

  // エピソードが選択されているかチェック
  const isEpisodeSelected = (episodeId: number) =>
    selectedItems.some((item) => item.type === 'episode' && item.id === episodeId)

  // プロダクト内のエピソードが一部選択されているかチェック
  const hasPartialEpisodeSelection = (product: Product) => {
    if (!product.episodes || product.episodes.length === 0) return false
    const selectedCount = product.episodes.filter((ep) => isEpisodeSelected(ep.episode_id)).length
    return selectedCount > 0 && selectedCount < product.episodes.length
  }

  // プロダクトのチェックボックスの状態を取得
  const getProductCheckState = (product: Product): boolean | 'indeterminate' => {
    if (isProductSelected(product.product_id)) return true
    if (hasPartialEpisodeSelection(product)) return 'indeterminate'
    return false
  }

  // エピソードのチェックボックスの状態を取得（プロダクトが選択されていればtrue）
  const getEpisodeCheckState = (productId: number, episodeId: number): boolean => {
    if (isProductSelected(productId)) return true
    return isEpisodeSelected(episodeId)
  }

  // エピソードのチェックボックスが無効かどうか（プロダクトが選択されていれば無効）
  const isEpisodeCheckDisabled = (productId: number): boolean => {
    return isProductSelected(productId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>アクセス設定</DialogTitle>
        </DialogHeader>

        {/* 選択済みアイテム */}
        {selectedItems.length > 0 && (
          <div className="shrink-0">
            <div className="text-sm font-medium">選択済み</div>
            <div className="max-h-32 border rounded-md p-2 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {selectedItems.map((item) => (
                  <Badge key={`${item.type}-${item.id}`} variant="secondary" className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">{item.type === 'product' ? 'P' : 'E'}</span>
                    {item.type === 'episode' && item.productTitle ? (
                      <span>
                        {item.productTitle} {item.title}
                      </span>
                    ) : (
                      <span>{item.title}</span>
                    )}
                    <span className="text-xs text-muted-foreground">(ID:{item.id})</span>
                    <button onClick={() => removeItem(item)} className="ml-1 p-0.5 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 検索フォーム */}
        <div className="flex gap-2 shrink-0">
          <Select value={domain} onValueChange={setDomain}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_DOMAINS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1 flex gap-2">
            <Input
              placeholder="タイトルで検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button variant="outline" onClick={handleSearch} disabled={loading}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 検索結果 */}
        <ScrollArea className="flex-1 min-h-0 border rounded-md">
          <div className="p-2">
            {loading ? (
              <div className="text-center text-muted-foreground py-8">読み込み中...</div>
            ) : products.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">プロダクトが見つかりません</div>
            ) : (
              products.map((product) => (
                <Collapsible
                  key={product.product_id}
                  open={expandedProducts.has(product.product_id)}
                  onOpenChange={() => toggleExpanded(product.product_id)}
                >
                  <div className="flex items-center gap-2 p-2 hover:bg-muted rounded">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={getProductCheckState(product)}
                        onCheckedChange={() => toggleProduct(product)}
                      />
                      <span className="text-sm">選択</span>
                    </label>
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center gap-1 flex-1 text-left px-2 py-1">
                        {expandedProducts.has(product.product_id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <span className="truncate">{product.title}</span>
                        <span className="text-xs text-muted-foreground ml-2">({product.episodes?.length || 0}話)</span>
                      </button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="ml-8 border-l pl-4 py-1">
                      {[...(product.episodes ?? [])]
                        .sort((a, b) => a.index - b.index)
                        .map((episode) => (
                          <div key={episode.episode_id} className="flex items-center gap-2 p-1 hover:bg-muted rounded">
                            <Checkbox
                              checked={getEpisodeCheckState(product.product_id, episode.episode_id)}
                              onCheckedChange={() => toggleEpisode(product, episode)}
                              disabled={isEpisodeCheckDisabled(product.product_id)}
                            />
                            <span className="text-sm">
                              #{episode.index}
                              {episode.description && (
                                <span className="text-muted-foreground ml-2">{episode.description}</span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">(ID:{episode.episode_id})</span>
                          </div>
                        ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </div>
        </ScrollArea>

        {/* アクション */}
        <div className="flex justify-end gap-2 mt-4 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button onClick={handleSave}>保存</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
